import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articleContent } from '@/mocks/articles';
import { allArticles } from '@/mocks/categories';

function ArticleNotFound() {
  return (
    <Layout>
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-600 mb-6">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug || !articleContent[slug]) {
    return <ArticleNotFound />;
  }

  const article = articleContent[slug];
  const relatedArticles = article.relatedSlugs
    .map((s) => allArticles.find((a) => a.slug === s))
    .filter(Boolean);

  return (
    <Layout>
      <SEOHead
        title={`${article.title} - 福利吧`}
        description={article.content.find((c) => c.type === 'paragraph')?.text?.slice(0, 150) || article.title}
        keywords={`${article.category},福利吧,优惠券,省钱,${article.title.slice(0, 20)}`}
        canonicalUrl={`https://fuliba.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.image,
        publisher: { '@type': 'Organization', name: '福利吧', url: 'https://fuliba.com' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fuliba.com' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://fuliba.com/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.category, to: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-3">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none">
              {article.content.map((block, index) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={index} className="text-xl font-bold text-foreground-950 mt-8 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'paragraph') {
                  return (
                    <p key={index} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'list') {
                  return (
                    <ul key={index} className="space-y-2 mb-4">
                      {block.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm md:text-base text-foreground-700">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === 'tip') {
                  return (
                    <div key={index} className="bg-accent-100/60 border-l-4 border-accent-500 rounded-r-lg p-4 my-6">
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-lightbulb-line text-accent-600"></i>
                        </div>
                        <p className="text-sm text-foreground-800">{block.text}</p>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-background-300/60">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-2">
                <i className="ri-links-line"></i>
                <span>文章标签：</span>
                <Link to={`/category/${article.categorySlug}`} className="text-primary-600 hover:text-primary-700 no-underline">
                  {article.category}
                </Link>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => ra && (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-200 no-underline cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <span className="text-xs text-foreground-500">{ra.date}</span>
                      <h3 className="font-semibold text-foreground-950 text-sm mt-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}